import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  ICreateLegalRequirementDependencyAction,
  ICreateLegalRequirementDependencyCondition,
  ICreateLegalRequirementDependencyConditionGroup,
  ICreateLegalRequirementDependencyRule,
  ILegalRequirementDependencyAction,
  ILegalRequirementDependencyByTransactionResponse,
  ILegalRequirementDependencyCondition,
  ILegalRequirementDependencyConditionGroup,
  ILegalRequirementDependencyOverview,
  ILegalRequirementDependencyRule,
} from '../../Interfaces/ILegalDependency';
import { customFetcher } from '../useFetch';

//////////////////// LegalRequirementDependencyRule
export const useGetLegalRequirementDependencyRules = () => {
  const {
    data: legalRequirementDependencyRules,
    isLoading: isLoadingLegalRequirementDependencyRules,
    refetch: refetchLegalRequirementDependencyRules,
  } = useQuery<ILegalRequirementDependencyRule[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalrequirementdependencyrule'],
    queryFn: () =>
      customFetcher({ endpoint: '/legalrequirementdependencyrule' }),
  });

  return {
    legalRequirementDependencyRules,
    isLoadingLegalRequirementDependencyRules,
    refetchLegalRequirementDependencyRules,
  };
};

export const useGetLegalRequirementDependencyOverview = (
  page: number,
  rowsPerPage: number,
  search: string,
  includeExecuted: boolean
) => {
  const {
    data: legalRequirementDependencyOverview,
    isLoading: isLoadingLegalRequirementDependencyOverview,
    isFetching: isFetchingLegalRequirementDependencyOverview,
    refetch: refetchLegalRequirementDependencyOverview,
  } = useQuery<ILegalRequirementDependencyOverview>({
    refetchOnWindowFocus: false,
    queryKey: [
      'legalrequirementdependencyrule',
      'overview',
      page,
      rowsPerPage,
      search,
      includeExecuted,
    ],
    placeholderData: keepPreviousData,
    queryFn: () =>
      customFetcher({
        endpoint: `/legalrequirementdependencyrule/overview?page=${page}&rowsPerPage=${rowsPerPage}&search=${encodeURIComponent(
          search
        )}&includeExecuted=${includeExecuted}`,
      }),
  });

  return {
    legalRequirementDependencyOverview,
    isLoadingLegalRequirementDependencyOverview,
    isFetchingLegalRequirementDependencyOverview,
    refetchLegalRequirementDependencyOverview,
  };
};

export const useGetLegalRequirementDependencyByTransaction = (
  idLegalTransaction: number | null | undefined,
  idLegalRequirement?: number | null,
  includeExecuted: boolean = true
) => {
  const {
    data: legalRequirementDependencyByTransaction,
    isLoading: isLoadingLegalRequirementDependencyByTransaction,
    isFetching: isFetchingLegalRequirementDependencyByTransaction,
    refetch: refetchLegalRequirementDependencyByTransaction,
  } = useQuery<ILegalRequirementDependencyByTransactionResponse>({
    refetchOnWindowFocus: false,
    queryKey: [
      'legalrequirementdependency',
      'by-transaction',
      idLegalTransaction,
      idLegalRequirement ?? null,
      includeExecuted,
    ],
    queryFn: () => {
      const searchParams = new URLSearchParams({
        idLegalTransaction: String(idLegalTransaction),
        includeExecuted: String(includeExecuted),
      });

      if (idLegalRequirement !== null && idLegalRequirement !== undefined) {
        searchParams.set('idLegalRequirement', String(idLegalRequirement));
      }

      return customFetcher({
        endpoint: `/legalrequirementdependency/by-transaction?${searchParams.toString()}`,
      });
    },
    enabled:
      idLegalTransaction !== null &&
      idLegalTransaction !== undefined &&
      idLegalTransaction > 0,
  });

  return {
    legalRequirementDependencyByTransaction,
    isLoadingLegalRequirementDependencyByTransaction,
    isFetchingLegalRequirementDependencyByTransaction,
    refetchLegalRequirementDependencyByTransaction,
  };
};

export const useGetLegalRequirementDependencyRuleById = (
  idLRDRule?: number | null
) => {
  const {
    data: legalRequirementDependencyRule,
    isLoading: isLoadingLegalRequirementDependencyRule,
    refetch: refetchLegalRequirementDependencyRule,
  } = useQuery<ILegalRequirementDependencyRule>({
    refetchOnWindowFocus: false,
    queryKey: ['legalrequirementdependencyrule', idLRDRule],
    queryFn: () =>
      customFetcher({
        endpoint: `/legalrequirementdependencyrule/${idLRDRule}`,
      }),
    enabled: idLRDRule !== null && idLRDRule !== undefined,
  });

  return {
    legalRequirementDependencyRule,
    isLoadingLegalRequirementDependencyRule,
    refetchLegalRequirementDependencyRule,
  };
};

export const useCreateLegalRequirementDependencyRule = () => {
  const {
    mutateAsync: createLegalRequirementDependencyRule,
    isPending: isPendingCreateLegalRequirementDependencyRule,
  } = useMutation({
    mutationFn: (rule: ICreateLegalRequirementDependencyRule) =>
      customFetcher({
        endpoint: '/legalrequirementdependencyrule',
        method: 'POST',
        body: rule,
      }),
  });

  return {
    createLegalRequirementDependencyRule,
    isPendingCreateLegalRequirementDependencyRule,
  };
};

export const useUpdateLegalRequirementDependencyRule = () => {
  const {
    mutateAsync: updateLegalRequirementDependencyRule,
    isPending: isPendingUpdateLegalRequirementDependencyRule,
  } = useMutation({
    mutationFn: (rule: ILegalRequirementDependencyRule) =>
      customFetcher({
        endpoint: '/legalrequirementdependencyrule',
        method: 'PUT',
        body: rule,
      }),
  });

  return {
    updateLegalRequirementDependencyRule,
    isPendingUpdateLegalRequirementDependencyRule,
  };
};

export const useDeleteLegalRequirementDependencyRule = () => {
  const {
    mutateAsync: deleteLegalRequirementDependencyRule,
    isPending: isPendingDeleteLegalRequirementDependencyRule,
  } = useMutation({
    mutationFn: (idLRDRule: number) =>
      customFetcher({
        endpoint: `/legalrequirementdependencyrule/${idLRDRule}`,
        method: 'DELETE',
      }),
  });

  return {
    deleteLegalRequirementDependencyRule,
    isPendingDeleteLegalRequirementDependencyRule,
  };
};

//////////////////// LegalRequirementDependencyConditionGroup
export const useGetLegalRequirementDependencyConditionGroups = () => {
  const {
    data: legalRequirementDependencyConditionGroups,
    isLoading: isLoadingLegalRequirementDependencyConditionGroups,
    refetch: refetchLegalRequirementDependencyConditionGroups,
  } = useQuery<ILegalRequirementDependencyConditionGroup[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalrequirementdependencyconditiongroup'],
    queryFn: () =>
      customFetcher({ endpoint: '/legalrequirementdependencyconditiongroup' }),
  });

  return {
    legalRequirementDependencyConditionGroups,
    isLoadingLegalRequirementDependencyConditionGroups,
    refetchLegalRequirementDependencyConditionGroups,
  };
};

export const useGetLegalRequirementDependencyConditionGroupById = (
  idLRDConditionGroup?: number | null
) => {
  const {
    data: legalRequirementDependencyConditionGroup,
    isLoading: isLoadingLegalRequirementDependencyConditionGroup,
    refetch: refetchLegalRequirementDependencyConditionGroup,
  } = useQuery<ILegalRequirementDependencyConditionGroup>({
    refetchOnWindowFocus: false,
    queryKey: ['legalrequirementdependencyconditiongroup', idLRDConditionGroup],
    queryFn: () =>
      customFetcher({
        endpoint: `/legalrequirementdependencyconditiongroup/${idLRDConditionGroup}`,
      }),
    enabled: idLRDConditionGroup !== null && idLRDConditionGroup !== undefined,
  });

  return {
    legalRequirementDependencyConditionGroup,
    isLoadingLegalRequirementDependencyConditionGroup,
    refetchLegalRequirementDependencyConditionGroup,
  };
};

export const useCreateLegalRequirementDependencyConditionGroup = () => {
  const {
    mutateAsync: createLegalRequirementDependencyConditionGroup,
    isPending: isPendingCreateLegalRequirementDependencyConditionGroup,
  } = useMutation({
    mutationFn: (
      conditionGroup: ICreateLegalRequirementDependencyConditionGroup
    ) =>
      customFetcher({
        endpoint: '/legalrequirementdependencyconditiongroup',
        method: 'POST',
        body: conditionGroup,
      }),
  });

  return {
    createLegalRequirementDependencyConditionGroup,
    isPendingCreateLegalRequirementDependencyConditionGroup,
  };
};

export const useUpdateLegalRequirementDependencyConditionGroup = () => {
  const {
    mutateAsync: updateLegalRequirementDependencyConditionGroup,
    isPending: isPendingUpdateLegalRequirementDependencyConditionGroup,
  } = useMutation({
    mutationFn: (conditionGroup: ILegalRequirementDependencyConditionGroup) =>
      customFetcher({
        endpoint: '/legalrequirementdependencyconditiongroup',
        method: 'PUT',
        body: conditionGroup,
      }),
  });

  return {
    updateLegalRequirementDependencyConditionGroup,
    isPendingUpdateLegalRequirementDependencyConditionGroup,
  };
};

export const useDeleteLegalRequirementDependencyConditionGroup = () => {
  const {
    mutateAsync: deleteLegalRequirementDependencyConditionGroup,
    isPending: isPendingDeleteLegalRequirementDependencyConditionGroup,
  } = useMutation({
    mutationFn: (idLRDConditionGroup: number) =>
      customFetcher({
        endpoint: `/legalrequirementdependencyconditiongroup/${idLRDConditionGroup}`,
        method: 'DELETE',
      }),
  });

  return {
    deleteLegalRequirementDependencyConditionGroup,
    isPendingDeleteLegalRequirementDependencyConditionGroup,
  };
};

//////////////////// LegalRequirementDependencyCondition
export const useGetLegalRequirementDependencyConditions = () => {
  const {
    data: legalRequirementDependencyConditions,
    isLoading: isLoadingLegalRequirementDependencyConditions,
    refetch: refetchLegalRequirementDependencyConditions,
  } = useQuery<ILegalRequirementDependencyCondition[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalrequirementdependencycondition'],
    queryFn: () =>
      customFetcher({ endpoint: '/legalrequirementdependencycondition' }),
  });

  return {
    legalRequirementDependencyConditions,
    isLoadingLegalRequirementDependencyConditions,
    refetchLegalRequirementDependencyConditions,
  };
};

export const useGetLegalRequirementDependencyConditionById = (
  idLRDCondition?: number | null
) => {
  const {
    data: legalRequirementDependencyCondition,
    isLoading: isLoadingLegalRequirementDependencyCondition,
    refetch: refetchLegalRequirementDependencyCondition,
  } = useQuery<ILegalRequirementDependencyCondition>({
    refetchOnWindowFocus: false,
    queryKey: ['legalrequirementdependencycondition', idLRDCondition],
    queryFn: () =>
      customFetcher({
        endpoint: `/legalrequirementdependencycondition/${idLRDCondition}`,
      }),
    enabled: idLRDCondition !== null && idLRDCondition !== undefined,
  });

  return {
    legalRequirementDependencyCondition,
    isLoadingLegalRequirementDependencyCondition,
    refetchLegalRequirementDependencyCondition,
  };
};

export const useCreateLegalRequirementDependencyCondition = () => {
  const {
    mutateAsync: createLegalRequirementDependencyCondition,
    isPending: isPendingCreateLegalRequirementDependencyCondition,
  } = useMutation({
    mutationFn: (condition: ICreateLegalRequirementDependencyCondition) =>
      customFetcher({
        endpoint: '/legalrequirementdependencycondition',
        method: 'POST',
        body: condition,
      }),
  });

  return {
    createLegalRequirementDependencyCondition,
    isPendingCreateLegalRequirementDependencyCondition,
  };
};

export const useUpdateLegalRequirementDependencyCondition = () => {
  const {
    mutateAsync: updateLegalRequirementDependencyCondition,
    isPending: isPendingUpdateLegalRequirementDependencyCondition,
  } = useMutation({
    mutationFn: (condition: ILegalRequirementDependencyCondition) =>
      customFetcher({
        endpoint: '/legalrequirementdependencycondition',
        method: 'PUT',
        body: condition,
      }),
  });

  return {
    updateLegalRequirementDependencyCondition,
    isPendingUpdateLegalRequirementDependencyCondition,
  };
};

export const useDeleteLegalRequirementDependencyCondition = () => {
  const {
    mutateAsync: deleteLegalRequirementDependencyCondition,
    isPending: isPendingDeleteLegalRequirementDependencyCondition,
  } = useMutation({
    mutationFn: (idLRDCondition: number) =>
      customFetcher({
        endpoint: `/legalrequirementdependencycondition/${idLRDCondition}`,
        method: 'DELETE',
      }),
  });

  return {
    deleteLegalRequirementDependencyCondition,
    isPendingDeleteLegalRequirementDependencyCondition,
  };
};

//////////////////// LegalRequirementDependencyAction
export const useGetLegalRequirementDependencyActions = () => {
  const {
    data: legalRequirementDependencyActions,
    isLoading: isLoadingLegalRequirementDependencyActions,
    refetch: refetchLegalRequirementDependencyActions,
  } = useQuery<ILegalRequirementDependencyAction[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalrequirementdependencyaction'],
    queryFn: () =>
      customFetcher({ endpoint: '/legalrequirementdependencyaction' }),
  });

  return {
    legalRequirementDependencyActions,
    isLoadingLegalRequirementDependencyActions,
    refetchLegalRequirementDependencyActions,
  };
};

export const useGetLegalRequirementDependencyActionById = (
  idLRDAction?: number | null
) => {
  const {
    data: legalRequirementDependencyAction,
    isLoading: isLoadingLegalRequirementDependencyAction,
    refetch: refetchLegalRequirementDependencyAction,
  } = useQuery<ILegalRequirementDependencyAction>({
    refetchOnWindowFocus: false,
    queryKey: ['legalrequirementdependencyaction', idLRDAction],
    queryFn: () =>
      customFetcher({
        endpoint: `/legalrequirementdependencyaction/${idLRDAction}`,
      }),
    enabled: idLRDAction !== null && idLRDAction !== undefined,
  });

  return {
    legalRequirementDependencyAction,
    isLoadingLegalRequirementDependencyAction,
    refetchLegalRequirementDependencyAction,
  };
};

export const useCreateLegalRequirementDependencyAction = () => {
  const {
    mutateAsync: createLegalRequirementDependencyAction,
    isPending: isPendingCreateLegalRequirementDependencyAction,
  } = useMutation({
    mutationFn: (action: ICreateLegalRequirementDependencyAction) =>
      customFetcher({
        endpoint: '/legalrequirementdependencyaction',
        method: 'POST',
        body: action,
      }),
  });

  return {
    createLegalRequirementDependencyAction,
    isPendingCreateLegalRequirementDependencyAction,
  };
};

export const useUpdateLegalRequirementDependencyAction = () => {
  const {
    mutateAsync: updateLegalRequirementDependencyAction,
    isPending: isPendingUpdateLegalRequirementDependencyAction,
  } = useMutation({
    mutationFn: (action: ILegalRequirementDependencyAction) =>
      customFetcher({
        endpoint: '/legalrequirementdependencyaction',
        method: 'PUT',
        body: action,
      }),
  });

  return {
    updateLegalRequirementDependencyAction,
    isPendingUpdateLegalRequirementDependencyAction,
  };
};

export const useDeleteLegalRequirementDependencyAction = () => {
  const {
    mutateAsync: deleteLegalRequirementDependencyAction,
    isPending: isPendingDeleteLegalRequirementDependencyAction,
  } = useMutation({
    mutationFn: (idLRDAction: number) =>
      customFetcher({
        endpoint: `/legalrequirementdependencyaction/${idLRDAction}`,
        method: 'DELETE',
      }),
  });

  return {
    deleteLegalRequirementDependencyAction,
    isPendingDeleteLegalRequirementDependencyAction,
  };
};
